// Queries screen, ported from renderQueriesView/openNewQueryModal/
// openQueryModal (source lines 7980-8101).
import { queriesApi, patientsApi, usersApi, studiesApi } from "./api-client.js";
import { escapeHtml, fmtDate, fmtDateOnly, openModal, closeModal, notify, $ } from "./dom.js";
import { canWriteSite, canManageQueries, canAnswerQuery } from "./state.js";
import { VISIT_CONFIG } from "@woof/shared";

let filterPid = "";
let filterStatus = "";

export async function renderQueriesView(root: HTMLElement): Promise<void> {
  root.innerHTML = `<div class="loading">Chargement…</div>`;
  const [all, patients, studies] = await Promise.all([queriesApi.list(), patientsApi.list(), studiesApi.list()]);
  const studyName = (id: number | null) => (id == null ? "—" : studies.find((s: any) => s.id === id)?.name ?? "—");
  let list = all;
  if (filterPid) list = list.filter((q: any) => q.patientId === filterPid);
  if (filterStatus) list = list.filter((q: any) => q.status === filterStatus);

  const patOpts = `<option value="">Tous patients</option>` + patients.map((p: any) => `<option value="${p.id}" ${filterPid === p.id ? "selected" : ""}>${p.id}</option>`).join("");
  const statusOpts = ["", "Ouverte", "Répondue", "Fermée"].map((o) => `<option value="${o}" ${filterStatus === o ? "selected" : ""}>${o || "Tous statuts"}</option>`).join("");

  const rows = list.length
    ? list
        .map(
          (q: any) => `<tr>
        <td>${escapeHtml(q.qid)}</td><td>${escapeHtml(q.patientId)}</td><td>${escapeHtml(studyName(q.studyId))}</td><td>${escapeHtml(q.visitKey || "")}</td>
        <td>${escapeHtml(q.field || "")}</td><td>${escapeHtml((q.question || "").slice(0, 80))}</td><td>${fmtDateOnly(q.createdAt)}</td>
        <td><span class="badge badge-${q.status === "Ouverte" ? "warning" : q.status === "Répondue" ? "info" : "success"}">${q.status}</span></td>
        <td><button class="btn btn-sm" data-open-q="${q.id}">Voir</button></td>
      </tr>`
        )
        .join("")
    : `<tr><td colspan="9" class="muted">Aucune query.</td></tr>`;

  const canCreate = canManageQueries() && patients.some((p: any) => canWriteSite(p.siteid));

  root.innerHTML = `
    <div class="view-header"><h2>❓ Queries</h2><p class="muted">${list.length} / ${all.length} queries</p></div>
    <div class="filters-bar">
      <select id="qFilterPid">${patOpts}</select>
      <select id="qFilterStatus">${statusOpts}</select>
      ${canCreate ? `<button class="btn btn-primary" id="btnNewQuery">+ Nouvelle query</button>` : ""}
    </div>
    <table class="data-table"><thead><tr><th>ID</th><th>Patient</th><th>Étude</th><th>Visite</th><th>Champ</th><th>Question</th><th>Créée</th><th>Statut</th><th></th></tr></thead><tbody>${rows}</tbody></table>`;

  $("#qFilterPid")!.addEventListener("change", (e) => { filterPid = (e.target as HTMLSelectElement).value; void renderQueriesView(root); });
  $("#qFilterStatus")!.addEventListener("change", (e) => { filterStatus = (e.target as HTMLSelectElement).value; void renderQueriesView(root); });
  $("#btnNewQuery")?.addEventListener("click", () => void openNewQueryModal(patients, root));
  root.querySelectorAll<HTMLElement>("[data-open-q]").forEach((btn) => btn.addEventListener("click", () => openQueryModal(btn.dataset.openQ!, root)));
}

async function openNewQueryModal(patients: any[], root: HTMLElement): Promise<void> {
  const users = await usersApi.directory();
  // Same "assigned to" behavior as deviations: real user picker excluding
  // admin (a system-management role, not someone queries get assigned to),
  // defaulting to the sponsor.
  const assignableUsers = users.filter((u: any) => u.role !== "ADMIN");
  const sponsorUser = assignableUsers.find((u: any) => u.role === "SPONSOR");
  const patOpts = `<option value="">— Sélectionner —</option>` + patients.map((p) => `<option value="${p.id}">${p.id}</option>`).join("");
  const visitOpts = VISIT_CONFIG.map((v) => `<option value="${v.vk}">${v.label}</option>`).join("");
  const assignedOpts = assignableUsers
    .map((u: any) => `<option value="${escapeHtml(u.fullName)}" ${sponsorUser && u.id === sponsorUser.id ? "selected" : ""}>${escapeHtml(u.fullName)} (${escapeHtml(u.role)})</option>`)
    .join("");
  openModal(
    "Nouvelle query",
    `<div class="form-grid">
      <div class="form-group"><label>Patient <span class="required-mark">*</span></label><select id="q_pid">${patOpts}</select></div>
      <div class="form-group"><label>Visite</label><select id="q_visit"><option value="">—</option>${visitOpts}</select></div>
      <div class="form-group"><label>Champ concerné</label><input id="q_field"></div>
      <div class="form-group" style="grid-column:1/-1"><label>Question <span class="required-mark">*</span></label><textarea id="q_question" rows="3"></textarea></div>
      <div class="form-group"><label>Assigné à</label><select id="q_assigned">${assignedOpts}</select></div>
    </div>`,
    `<button class="btn btn-secondary" id="qCancel">Annuler</button><button class="btn btn-primary" id="qSave">Créer</button>`
  );
  $("#qCancel")!.addEventListener("click", closeModal);
  $("#qSave")!.addEventListener("click", async () => {
    const patientId = ($("#q_pid") as HTMLSelectElement).value;
    if (!patientId) return notify("error", "Patient obligatoire");
    const question = ($("#q_question") as HTMLTextAreaElement).value.trim();
    if (!question) return notify("error", "Question obligatoire");
    await queriesApi.create({
      patientId,
      visitKey: ($("#q_visit") as HTMLSelectElement).value || undefined,
      field: ($("#q_field") as HTMLInputElement).value || undefined,
      question,
      assignedTo: ($("#q_assigned") as HTMLSelectElement).value || undefined,
    });
    closeModal();
    void renderQueriesView(root);
  });
}

// Status-aware action controls, replacing the old always-editable
// answer/status form: which action is available depends on the query's
// current status AND the caller's role, matching the backend's
// Ouverte -> Répondue -> Fermée state machine and its role gating
// (site staff answers; CRA/DM raises/closes/reopens/reassigns).
async function openQueryModal(id: string, root: HTMLElement): Promise<void> {
  const [q, studies] = await Promise.all([queriesApi.get(id), studiesApi.list()]);
  const studyName = q.studyId != null ? studies.find((s: any) => s.id === q.studyId)?.name ?? "—" : "—";
  const writable = canWriteSite(q.siteCode);
  const canAnswer = writable && canAnswerQuery() && q.status === "Ouverte";
  const canClose = writable && canManageQueries() && q.status === "Répondue";
  const canReopen = writable && canManageQueries() && q.status === "Fermée";
  const canReassign = writable && canManageQueries() && q.status !== "Fermée";

  const histHtml = (q.history || []).map((h: any) => `<div class="audit-entry"><strong>${fmtDate(h.ts)}</strong> — ${escapeHtml(h.user)} — ${escapeHtml(h.action)}: ${escapeHtml(h.comment || "")}</div>`).join("");
  const statusBadgeClass = q.status === "Ouverte" ? "warning" : q.status === "Répondue" ? "info" : "success";

  openModal(
    `Query ${q.qid}`,
    `<div class="form-grid">
      <div class="form-group"><label>Patient</label><div>${escapeHtml(q.patientId)}</div></div>
      <div class="form-group"><label>Étude</label><div>${escapeHtml(studyName)}</div></div>
      <div class="form-group"><label>Visite</label><div>${escapeHtml(q.visitKey || "—")}</div></div>
      <div class="form-group" style="grid-column:1/-1"><label>Question</label><div>${escapeHtml(q.question || "").replace(/\n/g, "<br>")}</div></div>
      <div class="form-group" style="grid-column:1/-1"><label>Réponse</label>${
        canAnswer
          ? `<textarea id="q_edit_answer">${escapeHtml(q.answer || "")}</textarea>`
          : `<div>${escapeHtml(q.answer || "—").replace(/\n/g, "<br>")}</div>`
      }</div>
      <div class="form-group"><label>Statut</label><div><span class="badge badge-${statusBadgeClass}">${escapeHtml(q.status)}</span></div></div>
      <div class="form-group"><label>Assigné à</label>${
        canReassign
          ? `<div style="display:flex;gap:6px"><input id="q_edit_assigned" value="${escapeHtml(q.assignedTo || "")}"><button class="btn btn-sm btn-secondary" id="qReassign">Réassigner</button></div>`
          : `<div>${escapeHtml(q.assignedTo || "—")}</div>`
      }</div>
    </div>
    ${histHtml ? `<h4 style="margin-top:14px">Historique</h4>${histHtml}` : ""}`,
    `<button class="btn btn-secondary" id="qCancel">Annuler</button>
     ${canAnswer ? `<button class="btn btn-primary" id="qAnswer">Répondre</button>` : ""}
     ${canClose ? `<button class="btn btn-primary" id="qCloseAction">Clôturer</button>` : ""}
     ${canReopen ? `<button class="btn btn-primary" id="qReopenAction">Réouvrir</button>` : ""}`,
    true
  );
  $("#qCancel")!.addEventListener("click", closeModal);
  $("#qAnswer")?.addEventListener("click", async () => {
    const answer = ($("#q_edit_answer") as HTMLTextAreaElement).value.trim();
    if (!answer) return notify("error", "La réponse est obligatoire");
    await queriesApi.answer(id, answer);
    closeModal();
    void renderQueriesView(root);
  });
  $("#qCloseAction")?.addEventListener("click", async () => {
    await queriesApi.close(id);
    closeModal();
    void renderQueriesView(root);
  });
  $("#qReopenAction")?.addEventListener("click", async () => {
    const comment = prompt("Raison de la réouverture (facultatif) :") || undefined;
    await queriesApi.reopen(id, comment);
    closeModal();
    void renderQueriesView(root);
  });
  $("#qReassign")?.addEventListener("click", async () => {
    const assignedTo = ($("#q_edit_assigned") as HTMLInputElement).value.trim();
    if (!assignedTo) return notify("error", "Assigné à est obligatoire");
    await queriesApi.reassign(id, assignedTo);
    closeModal();
    void renderQueriesView(root);
  });
}
